import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertCircle, Info, X } from 'lucide-react';

// Toast context for global toast management
let toastId = 0;
let toastCallbacks = [];

// Global toast methods
export const toast = {
  success: (message, duration = 3000) => {
    showToast({ type: 'success', message, duration });
  },
  error: (message, duration = 5000) => {
    showToast({ type: 'error', message, duration });
  },
  info: (message, duration = 3000) => {
    showToast({ type: 'info', message, duration });
  },
  warning: (message, duration = 4000) => {
    showToast({ type: 'warning', message, duration });
  }
};

const showToast = (toast) => {
  const id = ++toastId;
  const toastWithId = { ...toast, id };
  
  toastCallbacks.forEach(callback => callback(toastWithId));
};

// Toast item component
const ToastItem = ({ toast, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger entrance animation
    const timer = setTimeout(() => setIsVisible(true), 10);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (toast.duration > 0) {
      const timer = setTimeout(() => {
        handleRemove();
      }, toast.duration);
      return () => clearTimeout(timer);
    }
  }, [toast.duration]);

  const handleRemove = () => {
    setIsVisible(false);
    setTimeout(() => onRemove(toast.id), 300);
  };

  const getIcon = () => {
    switch (toast.type) {
      case 'success':
        return <CheckCircle size={20} />;
      case 'error':
        return <AlertCircle size={20} />;
      case 'warning':
        return <AlertCircle size={20} />;
      case 'info':
      default:
        return <Info size={20} />;
    }
  };

  return (
    <div 
      className={`toast-item toast-${toast.type} ${isVisible ? 'visible' : ''}`}
    >
      <div className={`toast-icon ${toast.type}`}>
        {getIcon()}
      </div>
      <div className="toast-message">
        {toast.message}
      </div>
      <button 
        className="toast-close"
        onClick={handleRemove}
      >
        <X size={16} />
      </button>
    </div>
  );
};

// Main toast container component
const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const callback = (toast) => {
      setToasts(prev => [...prev, toast]);
    };

    toastCallbacks.push(callback);

    return () => {
      toastCallbacks = toastCallbacks.filter(cb => cb !== callback);
    };
  }, []);

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  return (
    <div className="toast-container">
      {toasts.map(toast => (
        <ToastItem 
          key={toast.id} 
          toast={toast} 
          onRemove={removeToast}
        />
      ))}
    </div>
  );
};

export default ToastContainer;
